import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  DialogContentText,
  Alert,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { postVendorLinkForm, getIntegrationsForVendor } from '../../../../api/warehouse-vendors';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { AwaitButton } from '../../../../reusable-components/await-button';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';

WarehouseVendorLinkModal.propTypes = {
  onClose: PropTypes.func,
  row: PropTypes.object,
  data: PropTypes.object,
  fetchData: PropTypes.func,
  open: PropTypes.bool,
};


const IntegrationInput = ({ title, id, options, autoCompleteBaseOps, value, setValue }) => options?.length > 0 ? <>
  <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>{title}</DialogContentText>
  <Autocomplete
    {...autoCompleteBaseOps}
    value={options.find((opt) => opt.value === value[id]) ?? null}
    onChange={(event, newValue) => {
      value[id] = newValue?.value;
      setValue({ ...value });
    }}
    options={options}
  />
</> : "";

export default function WarehouseVendorLinkModal({ onClose, row, data, fetchData, open }) {
  const { enqueueSnackbar } = useSnackbar();
  const [value, setValue] = useState({});
  const [integrations, setIntegrations] = useState(null);

  useEffect(() => {
    setValue({
      id: row?.id,
      siteId: row?.siteId,
      siteVendorId: row?.siteVendorId,
      warehouseVendorId: row?.warehouseVendorId,
      cxmlConfigId: row?.cxmlConfigId,
      punchoutConfigId: row?.punchoutConfigId,
      cartCheckConfigId: row?.cartCheckConfigId,
      jsonOrderConfigId: row?.jsonOrderConfigId,
      excelOrderImportConfigId: row?.excelOrderImportConfigId,
      excelInvoiceConfigId: row?.excelInvoiceConfigId,
      excelCreditMemoConfigId: row?.excelCreditMemoConfigId,
      ediConfigId: row?.ediConfigId,
      psIsaId: "",
      showPsIsaId: false,
      mainAccount: row?.mainAccount,
      integrationFieldValues: []
    });
    if (row) getIntegrations(row.warehouseVendorId);
  }, [row]);

  useEffect(() => {
    setValue({
      ...value,
      psIsaId: integrations?.psIsaIds?.find(x => x.siteId === row?.siteId && x.mainPurchasingVendorId === row?.warehouseVendorId)?.value,
      showPsIsaId: integrations?.showPsIsaId,
      integrationFieldValues: integrations?.integrationFieldValues?.filter(x => x.purchasingVendorSiteLinkId === row?.id)
    });
  }, [integrations]);


  const handleSave = async () => {
    const response = await postVendorLinkForm(value);
    if (response.data?.error) enqueueSnackbar(`Error: ${response.data.error}`, { variant: 'error' });
    else if (!response.data) enqueueSnackbar(`Failed to ${row ? 'update' : 'add'} warehouse vendor link`, { variant: 'error' });
    else {
      enqueueSnackbar(`Warehouse vendor link ${row ? 'updated' : 'added'} successfully`, { variant: 'success' });
      onClose();
      fetchData();
    }
  };

  const siteList = data?.sites || [];
  const vendorList = data?.vendorList?.map((v) => ({ label: v?.name, value: v?.id })) || [];
  const siteVendorList =
    data?.siteVendors
      .filter((sv) => sv.siteId === value.siteId)
      ?.map((vendor) => ({
        label: vendor?.vendorName,
        value: vendor?.vendorId,
      })) || [];

  const autoCompleteBaseOps = {
    fullWidth: true,
    size: 'small',
    isOptionEqualToValue: (option, value) => option?.value === value?.value,
    renderInput: (params) => <TextField {...params} label="Search or select" autoComplete="off"/>,
    sx: {
      mt: 0.5,
      '& legend': { display: 'none' },
      '& fieldset': { top: 0 },
      '& .MuiFormLabel-root ': { display: 'none' },
    },
  };

  const duplicateLink = data?.links.some(
    (x) =>
      x.siteId === value.siteId &&
      x.id !== row?.id &&
      (x.warehouseVendorId === value.warehouseVendorId || x.siteVendorId === value.siteVendorId)
  );

  const getIntegrations = async (warehouseVendorId) => {
    if (!warehouseVendorId) {
      setIntegrations(null);
      return;
    }
    const response = await getIntegrationsForVendor(warehouseVendorId);
    if (response.status === 200) setIntegrations(response.data);
    else {
      enqueueSnackbar(`Failed to fetch vendor integrations`, { variant: 'error' });
      setIntegrations(null);
    }
  };

  const integrationTypes = [
    { typeId: 7, isEnabled: value?.ediConfigId > 0 },
    { typeId: 1, isEnabled: value?.cxmlConfigId > 0 }
  ];

  const enabledIntegrationTypeIds = integrationTypes
    .filter(t => t.isEnabled)
    .map(t => t.typeId);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ textAlign: 'center' }}>
        {row ? 'Edit' : 'Add'} Vendor Link
        <CloseIconButton onClick={onClose} />
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Warehouse Vendor *</DialogContentText>
        <Autocomplete
          {...autoCompleteBaseOps}
          value={vendorList.find((opt) => opt.value === value.warehouseVendorId) ?? null}
          onChange={(event, newValue) => {
            setValue({ ...value, warehouseVendorId: newValue?.value });
            getIntegrations(newValue?.value);
          }}
          disabled={row}
          options={vendorList}
        />
        <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Site *</DialogContentText>
        <Autocomplete
          {...autoCompleteBaseOps}
          value={siteList.find((opt) => opt.value === value.siteId) ?? null}
          onChange={(event, newValue) => setValue({ ...value, siteId: newValue?.value, siteVendorId: null })}
          options={data?.sites || []}
          disabled={row}
        />
        <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Site Vendor *</DialogContentText>
        <Autocomplete
          {...autoCompleteBaseOps}
          value={siteVendorList.find((opt) => opt.value === value.siteVendorId) ?? null}
          onChange={(event, newValue) => {
            setValue({ ...value, siteVendorId: newValue?.value });
          }}
          disabled={row}
          options={siteVendorList}
        />
        <IntegrationInput options={integrations?.cxmlOrderConfigs} title="cXML Order Integration" id="cxmlConfigId" value={value} setValue={setValue} autoCompleteBaseOps={autoCompleteBaseOps} />
        <IntegrationInput options={integrations?.punchoutConfigs} title="Punchout Integration" id="punchoutConfigId" value={value} setValue={setValue} autoCompleteBaseOps={autoCompleteBaseOps} />
        <IntegrationInput options={integrations?.cartCheckConfigs} title="Cart Check Integration" id="cartCheckConfigId" value={value} setValue={setValue} autoCompleteBaseOps={autoCompleteBaseOps} />
        <IntegrationInput options={integrations?.jsonOrderConfigs} title="Json Order Integration" id="jsonOrderConfigId" value={value} setValue={setValue} autoCompleteBaseOps={autoCompleteBaseOps} />
        <IntegrationInput options={integrations?.excelOrderImportConfigs} title="Excel Order Import Integration" id="excelOrderImportConfigId" value={value} setValue={setValue} autoCompleteBaseOps={autoCompleteBaseOps} />
        <IntegrationInput options={integrations?.excelInvoiceConfigs} title="Excel Invoice Integration" id="excelInvoiceConfigId" value={value} setValue={setValue} autoCompleteBaseOps={autoCompleteBaseOps} />
        <IntegrationInput options={integrations?.excelCreditMemoConfigs} title="Excel Credit Memo Integration" id="excelCreditMemoConfigId" value={value} setValue={setValue} autoCompleteBaseOps={autoCompleteBaseOps} />
        <IntegrationInput options={integrations?.ediOrderConfigs} title="EDI Order Integration" id="ediConfigId" value={value} setValue={setValue} autoCompleteBaseOps={autoCompleteBaseOps} />
        {integrations?.showPsIsaId && integrations?.ediOrderConfigs?.length > 0 && <>
          <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>PS ISA Id</DialogContentText>
          <TextField
            value={value.psIsaId}
            fullWidth
            onChange={(event) => setValue({ ...value, psIsaId: event.target.value })}
            variant="outlined"
            autoComplete="off"
            size="small"
          />
        </>}

        {integrations?.integrationFieldTypes?.length > 0 &&
          integrations.integrationFieldTypes
            .filter(field => enabledIntegrationTypeIds.includes(field.integrationTypeId))
            .map((field) => {
              const { id, name, hoverText } = field;

              const fieldValueIndex = value.integrationFieldValues?.findIndex(x => x.integrationFieldId === id);
              const fieldValue = value.integrationFieldValues?.[fieldValueIndex]?.value ?? "";

              return <>
                <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>{name}</DialogContentText>
                <Tooltip title={hoverText} placement="top" arrow key={id}>
                  <TextField
                    type="text"
                    size="small"
                    fullWidth
                    value={fieldValue}
                    autoComplete="off"
                    onChange={(e) => {
                      const newExtraFields = [...(value.integrationFieldValues || [])];
                      if (fieldValueIndex > -1) newExtraFields[fieldValueIndex] = { ...newExtraFields[fieldValueIndex], value: e.target.value };
                      else newExtraFields.push({ integrationFieldId: id, value: e.target.value });
                      setValue({ ...value, integrationFieldValues: newExtraFields });
                    }}
                  />
                </Tooltip>
              </>;
            })}
        <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Main Account</DialogContentText>
        <Tooltip title={"Account used for autofill and catalog updates"} placement="left" arrow>
          <TextField
            sx={{ fontWeight: 'bold' }}
            size="small"
            value={value.mainAccount}
            onChange={(event) => setValue({ ...value, mainAccount: event.target.value })}
            autoComplete="off"
            fullWidth
            variant="outlined"
            />
        </Tooltip>
      </DialogContent>

      {duplicateLink && open && <Alert severity="error">Duplicate vendor link</Alert>}
      <DialogActions>
        <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={onClose}>
          Cancel
        </Button>
        <AwaitButton
          variant="contained"
          color="primary"
          size="medium"
          sx={{ width: 150 }}
          onClick={handleSave}
          disabled={!value.siteId || !value.warehouseVendorId || duplicateLink}
        >
          {row ? 'Save' : 'Add Link'}
        </AwaitButton>
      </DialogActions>
    </Dialog>
  );
}
